/**
 * ImagePreview Component
 * Lightbox modal for viewing images with zoom
 * Features:
 * - Full-size image display
 * - Click to zoom functionality
 * - Image dimensions display
 * - File size display
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Download,
  Image as ImageIcon,
  Info,
  X,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface ImagePreviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  imageUrl: string
  altText?: string
  width?: number
  height?: number
  fileSize?: number
  mimeType?: string
}

// ============================================
// Helper Functions
// ============================================

function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown size'
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// ============================================
// ImagePreview Component
// ============================================

export function ImagePreview({
  open,
  onOpenChange,
  imageUrl,
  altText,
  width,
  height,
  fileSize,
  mimeType,
}: ImagePreviewProps) {
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [showInfo, setShowInfo] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setZoom(1)
      setPosition({ x: 0, y: 0 })
      setShowInfo(false)
    }
  }, [open])

  // Handle zoom in
  const handleZoomIn = useCallback(() => {
    setZoom(z => Math.min(z + 0.25, 4))
  }, [])

  // Handle zoom out
  const handleZoomOut = useCallback(() => {
    setZoom(z => {
      const newZoom = Math.max(z - 0.25, 0.5)
      // Reset position if zooming out to fit
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 })
      }
      return newZoom
    })
  }, [])

  // Handle reset
  const handleReset = useCallback(() => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 1) return
    e.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }, [zoom, position])

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || zoom <= 1) return
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    })
  }, [isDragging, dragStart, zoom])

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Handle mouse leave to stop dragging
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Handle wheel for zooming
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    if (e.deltaY < 0) {
      handleZoomIn()
    } else {
      handleZoomOut()
    }
  }, [handleZoomIn, handleZoomOut])

  // Handle download
  const handleDownload = useCallback(() => {
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = altText || 'image'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [imageUrl, altText])

  // Double click to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom === 1) {
      setZoom(2)
    } else {
      handleReset()
    }
  }, [zoom, handleReset])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden bg-black/95">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-white/70" />
            <span className="text-sm text-white/90 font-medium truncate max-w-[200px]">
              {altText || 'Image Preview'}
            </span>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0"
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs text-white/70 w-12 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 4}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0"
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <div className="w-px h-5 bg-white/20 mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={zoom === 1 && position.x === 0 && position.y === 0}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0"
              title="Reset View"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowInfo(!showInfo)}
              className={cn(
                'text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0',
                showInfo && 'bg-white/10 text-white'
              )}
              title="Image Info"
            >
              <Info className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
            <div className="w-px h-5 bg-white/20 mx-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg h-8 w-8 p-0"
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Image Container */}
        <div
          ref={containerRef}
          className={cn(
            'relative w-full h-[80vh] flex items-center justify-center overflow-hidden',
            zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in',
            isDragging && 'cursor-grabbing'
          )}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
        >
          <img
            src={imageUrl}
            alt={altText || 'Preview'}
            className="max-w-full max-h-full object-contain select-none transition-transform duration-150"
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            }}
            draggable={false}
          />
        </div>

        {/* Info Panel */}
        {showInfo && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/80">
              {width && height && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/50">Dimensions:</span>
                  <span>{width} x {height}px</span>
                </div>
              )}
              {fileSize && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/50">Size:</span>
                  <span>{formatFileSize(fileSize)}</span>
                </div>
              )}
              {mimeType && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/50">Type:</span>
                  <span className="uppercase">{mimeType.split('/')[1]}</span>
                </div>
              )}
              {altText && (
                <div className="flex items-center gap-1.5">
                  <span className="text-white/50">Alt:</span>
                  <span className="truncate max-w-[200px]">{altText}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Zoom Instructions (shown briefly) */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/50 pointer-events-none">
          Double-click or scroll to zoom. Drag to pan when zoomed in.
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImagePreview
